import React, { useState } from 'react'

import {
  Text,
  IconButton,
  Input,
  HStack,
  Stack,
  Checkbox,
  Card,
  Radio,
  RadioGroup,
  Link,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Box,
} from '@chakra-ui/react'
import { FileSelectButton } from 'src/components/FileSelectButton/FileSelectButton'
import {
  CheckDone,
  Delete,
  Edit,
  ExternalLink,
  MessageSuccess,
} from 'src/components/icon'
import { InformationMessage } from 'src/components/InformationMessage/InformationMessage'
import { RequiredBadge } from 'src/components/RequiredBadge/RequiredBadge'
import { useSpinner } from 'src/hooks/use-spinner'
import type { components } from 'src/lib/api-client/schema.gen'
import { colors } from 'src/lib/chakra-theme/foundations/colors'

import {
  IcfDocumentType,
  icfDocumentTypes,
  icfDocumentTypeText,
  IcfDocument,
  isDocuSignType,
  SignField,
  icfDocumentSignFieldTypes,
  icfDocumentSignFieldTypeText,
} from '../entity'
import { IcfDocumentErrors } from '../validate'
import { IcfDocumentRevisionStatusBadge } from './icf-document-status-badge'

// 各項目の中身を見えやすくするための helper-type
type Identity<T> = T extends {}
  ? {
      [P in keyof T]: T[P]
    }
  : never

type Props =
  Identity<NewProps> | Identity<EditProps> | Identity<AddRevisionProps>

type PropsBase = {
  name: string
  documentType: IcfDocumentType
  fileName: string
  version: string
  status?: components['schemas']['output.IcfRevStatus']
  isRequiredPatientSign: boolean
  filePreviewUrl?: string
  signFields?: SignField[]
  errors?: IcfDocumentErrors
  hasTempFile: boolean
}

type OnChangeProps = {
  onChangeVersion: (value: string) => void
  onChangeName: (value: string) => void
  onChangeDocumentType: (type: IcfDocumentType) => void
  onSelectFile: (file: File) => Promise<void>
  onChangeRequiredPatientSign: () => void
}

type NewProps = {
  cardType: 'new'
  numberingId?: undefined
  onResetFile: () => void
} & OnChangeProps &
  Partial<PropsBase>

type EditProps = {
  cardType: 'edit'
  numberingId: IcfDocument['numberingId']
  canChangeDocumentType: boolean
  hasMultipleRevisionHistories: boolean
  canSetDocuSignRole: boolean
  hasSetDocuSignRole: boolean
  onRedirectDocuSignEdit: () => void
  openRevisionHistory: () => void
} & OnChangeProps &
  PropsBase

// 文書タイプ以外変更可能
type AddRevisionProps = {
  cardType: 'addRevision'
  numberingId: IcfDocument['numberingId']
  hasMultipleRevisionHistories: boolean
  openRevisionHistory: () => void
} & Omit<OnChangeProps, 'onChangeDocumentType'> &
  PropsBase

const isDocumentType = (num: number): num is IcfDocumentType => {
  return (
    Object.values(icfDocumentTypes).find(
      documentType => documentType === num,
    ) !== undefined
  )
}

const documentTypeDescriptionMap: Record<IcfDocumentType, string> = {
  [icfDocumentTypes.Undefined]: '',
  [icfDocumentTypes.AgreementForm]: 'DocuSignで署名ありの文書を登録します。',
  [icfDocumentTypes.CheckUnderstanding]:
    'DocuSignで理解度確認文書を登録します。',
  [icfDocumentTypes.Description]: '説明文書を登録します。',
  [icfDocumentTypes.Video]: '説明動画ファイルを登録します。',
}

export const IcfDocumentCardRenewal: React.FC<Props> = props => {
  const [canSelectFile, setCanSelectFile] = useState(
    props.cardType === 'new' || props.fileName === '',
  )

  const { showSpinner, hideSpinner } = useSpinner()

  const radioItems = (() => {
    const allItems = Object.values(icfDocumentTypes)
      .filter(documentType => documentType !== icfDocumentTypes.Undefined)
      .map(documentType => ({
        name: icfDocumentTypeText[documentType],
        value: documentType,
      }))
    // 理解度確認は使用していないので削除する
    return allItems.filter(
      item => item.value !== icfDocumentTypes.CheckUnderstanding,
    )
  })()

  const canEditDocumentType =
    (props.cardType === 'new' && !props.fileName) ||
    (props.cardType === 'edit' && props.canChangeDocumentType)

  const onChangeDocumentTypeRadioValue = (value: string) => {
    if (!(
      props.cardType === 'new' ||
      (props.cardType === 'edit' && props.canChangeDocumentType)
    )) {
      return
    }

    const num = Number(value)
    if (isDocumentType(num)) {
      props.onChangeDocumentType(num)
    }
  }

  const startFileEdit = () => {
    setCanSelectFile(true)
  }

  const completeFileEdit = () => {
    setCanSelectFile(false)
  }

  const resetFile = () => {
    if (props.cardType !== 'new') {
      return
    }

    props.onResetFile()
    setCanSelectFile(true)
  }

  return (
    <Card as="section" pos="relative" w="full" p="6" aria-label="document-card">
      <Stack spacing="8">
        {/* Dr承認機能撤廃後 */}
        {!!props.status && (
          <IcfDocumentRevisionStatusBadge status={props.status} />
        )}
        <HStack spacing="10">
          {props.numberingId !== undefined && (
            <Stack spacing="2" w="max-content">
              <Text color="gray.600" fontSize="sm" fontWeight="medium">
                文書ID
              </Text>
              <Text lineHeight="10">{props.numberingId}</Text>
            </Stack>
          )}

          <FormControl flex="1" isRequired isInvalid={!!props.errors?.version}>
            <FormLabel requiredIndicator={<RequiredBadge ml="1" />}>
              版数
            </FormLabel>
            <HStack spacing="4">
              <Input
                width="20"
                value={props.version ?? ''}
                onChange={e => props.onChangeVersion(e.target.value)}
                placeholder="1.0"
              />
              {props.cardType !== 'new' &&
                props.hasMultipleRevisionHistories && (
                  <Button variant="text" onClick={props.openRevisionHistory}>
                    改版履歴
                  </Button>
                )}
            </HStack>
            {!!props.errors?.version && (
              <FormErrorMessage>{props.errors.version}</FormErrorMessage>
            )}
          </FormControl>
        </HStack>

        {/* 文書名 */}
        <FormControl isRequired isInvalid={!!props.errors?.name}>
          <FormLabel requiredIndicator={<RequiredBadge ml="1" />}>
            文書名
          </FormLabel>
          <Input
            w="full"
            value={props.name ?? ''}
            onChange={e => props.onChangeName(e.target.value)}
          />
          {props.errors?.name && (
            <FormErrorMessage>{props.errors.name}</FormErrorMessage>
          )}
        </FormControl>

        {/* 文書の種類 */}
        <Box>
          <FormControl
            isInvalid={!!props.errors?.documentType}
            isRequired={
              props.cardType === 'edit' || props.cardType === 'addRevision'
            }
          >
            <FormLabel
              requiredIndicator={
                (props.cardType === 'edit' ||
                  props.cardType === 'addRevision') && <RequiredBadge ml="1" />
              }
            >
              文書の種類
            </FormLabel>
            <RadioGroup
              isDisabled={!canEditDocumentType}
              onChange={onChangeDocumentTypeRadioValue}
              value={String(props.documentType ?? '')}
            >
              <HStack spacing="6">
                {radioItems.map(item => (
                  <Radio key={item.value} value={String(item.value)}>
                    {item.name}
                  </Radio>
                ))}
              </HStack>
            </RadioGroup>
            {props.errors?.documentType && (
              <FormErrorMessage>{props.errors.documentType}</FormErrorMessage>
            )}
          </FormControl>

          {props.documentType !== undefined && (
            <Text fontSize="sm" mt="2">
              {documentTypeDescriptionMap[props.documentType]}
            </Text>
          )}
          {props.documentType === icfDocumentTypes.AgreementForm && (
            <Checkbox
              mt="4"
              onChange={props.onChangeRequiredPatientSign}
              isChecked={!!props.isRequiredPatientSign}
            >
              患者の同意必須
            </Checkbox>
          )}
        </Box>

        {/* ファイル */}
        {!!props.documentType && (
          <Stack spacing="2">
            <HStack spacing="1">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                ファイル名
              </Text>
              {(props.cardType === 'edit' ||
                props.cardType === 'addRevision') && <RequiredBadge ml="1" />}
            </HStack>
            <HStack spacing="4" align="center">
              {canSelectFile || props.errors?.fileSize ? (
                <Input
                  w="full"
                  maxWidth="300px"
                  placeholder={
                    props.documentType === icfDocumentTypes.Video
                      ? 'mp4ファイルを選択してください'
                      : 'PDFファイルを選択してください'
                  }
                  value={props.fileName ?? ''}
                  isReadOnly
                  isInvalid={
                    !!props.errors?.fileName || !!props.errors?.fileSize
                  }
                />
              ) : (
                // NOTE:動画のコンバートに時間がかかるため、アップロード直後にリンクをクリックされるとエラーになる。
                // それを防ぐため、動画の場合はリンクを付けない。
                <HStack align="center" spacing="2">
                  {props.hasTempFile &&
                  props.documentType === icfDocumentTypes.Video ? (
                    <Text fontSize="sm">{props.fileName}</Text>
                  ) : (
                    <Link
                      href={props.filePreviewUrl}
                      isExternal
                      color="blue.500"
                      rel="noopener noreferrer"
                      lineHeight="9"
                    >
                      <HStack as="span">
                        <span>{props.fileName}</span>
                        <Box minWidth="max-content">
                          <ExternalLink size="md" />
                        </Box>
                      </HStack>
                    </Link>
                  )}

                  {props.fileName && (
                    <IconButton
                      aria-label="edit"
                      variant="customIconButtonGhost"
                      icon={<Edit />}
                      onClick={startFileEdit}
                    />
                  )}
                  {props.cardType === 'new' && props.fileName && (
                    <IconButton
                      aria-label="trash"
                      variant="customIconButtonGhost"
                      icon={<Delete />}
                      onClick={resetFile}
                    />
                  )}
                </HStack>
              )}
              {(canSelectFile || !!props.errors?.fileSize) && (
                <HStack spacing="4">
                  <FileSelectButton
                    buttonProps={{
                      variant: 'outline',
                    }}
                    accept={
                      props.documentType === icfDocumentTypes.Video
                        ? '.mp4'
                        : '.pdf'
                    }
                    onChange={async file => {
                      showSpinner()
                      try {
                        await props.onSelectFile(file)
                        hideSpinner()
                        if (!props.errors?.fileSize) {
                          completeFileEdit()
                        }
                      } catch {
                        hideSpinner()
                      }
                    }}
                  />
                  {canSelectFile && !!props.fileName && (
                    <IconButton
                      aria-label="cancel"
                      variant="customIconButtonGhost"
                      icon={<CheckDone />}
                      onClick={completeFileEdit}
                    />
                  )}
                </HStack>
              )}
            </HStack>
            {props.errors?.fileName && (
              <ErrorMessage errorMessage={props.errors.fileName} />
            )}
            {props.errors?.fileSize && (
              <ErrorMessage errorMessage={props.errors.fileSize} />
            )}
            {!canSelectFile &&
              props.hasTempFile &&
              props.documentType === icfDocumentTypes.AgreementForm &&
              (props.cardType === 'new' ||
                props.cardType === 'addRevision') && (
                <InformationMessage message="署名欄は登録処理後、文書編集ページで設定が必要です。" />
              )}
          </Stack>
        )}

        {!!props.documentType &&
          isDocuSignType(props.documentType) &&
          props.fileName &&
          props.documentType === icfDocumentTypes.AgreementForm &&
          !!props.signFields &&
          !canSelectFile && (
            <Stack spacing="2">
              <HStack spacing={1}>
                <Text fontWeight="medium" fontSize="sm" color="gray.600">
                  署名ロール
                </Text>
                <RequiredBadge ml="1" />
              </HStack>
              <HStack spacing="8">
                {props.documentType === icfDocumentTypes.AgreementForm &&
                  !!props.signFields &&
                  !canSelectFile && (
                    <HStack spacing="4">
                      {icfDocumentSignFieldTypes.map(role => {
                        const isSetSignField = props.signFields?.some(
                          signField => signField.signFieldType === role,
                        )
                        const isOptional =
                          props.signFields?.find(
                            signField => signField.signFieldType === role,
                          )?.optional ?? false
                        return (
                          <HStack key={role} spacing="1">
                            {isSetSignField && (
                              <MessageSuccess color={colors.green[500]} />
                            )}
                            <Text
                              key={role}
                              fontSize="sm"
                              color={isSetSignField ? 'gray.800' : 'gray.200'}
                              fontWeight="bold"
                              as="span"
                            >
                              {icfDocumentSignFieldTypeText[role]}
                              {isOptional && <span>（任意）</span>}
                            </Text>
                          </HStack>
                        )
                      })}
                      {props.hasTempFile && props.cardType === 'edit' && (
                        <InformationMessage message="署名欄は一時保存後、設定することができます。" />
                      )}
                    </HStack>
                  )}
                {props.cardType === 'edit' &&
                  props.canSetDocuSignRole &&
                  props.onRedirectDocuSignEdit && (
                    <Button
                      padding="0"
                      alignSelf="start"
                      variant="link"
                      onClick={props.onRedirectDocuSignEdit}
                      isDisabled={canSelectFile}
                      minWidth="auto"
                    >
                      {props.hasSetDocuSignRole ? (
                        <HStack padding={0}>
                          <Text>設定更新</Text>
                          <ExternalLink />
                        </HStack>
                      ) : (
                        <HStack>
                          <Text>項目設定</Text>
                          <ExternalLink />
                        </HStack>
                      )}
                    </Button>
                  )}
              </HStack>
            </Stack>
          )}
      </Stack>
    </Card>
  )
}

const ErrorMessage: React.FC<{ errorMessage: string }> = ({ errorMessage }) => {
  return (
    <Text color="red.500" fontSize="sm" mt="2">
      {errorMessage}
    </Text>
  )
}
